import React from 'react';
import { Checkbox, Chip } from '@heroui/react';
import { TriangleAlert } from 'lucide-react';
import AttachmentList from './AttachmentList.js';

/**
 * EndorsementCard — one endorsement: the claim, and the proof.
 *
 * The legacy page made the pairing explicit and this keeps it: ticking the box
 * without attaching anything is called out in red, because that combination is
 * what makes an endorsement not count. The card border carries the same signal
 * so the state is readable without reading.
 */
function EndorsementCard({ type, checked, record, onToggle, onDeleteAsset, onUploadAsset, busy }) {
	const assets = Array.isArray(record?.attachments) ? record.attachments : [];
	const missingProof = checked && assets.length === 0;

	return (
		<section
			className={[
				'bg-content1 rounded-xl p-4 sm:p-5 flex flex-col gap-3.5 border',
				missingProof ? 'border-danger' : 'border-default-200'
			].join(' ')}
		>
			<header className="flex items-start justify-between gap-3">
				<Checkbox isSelected={checked} onValueChange={() => onToggle(type.key)} isDisabled={busy}>
					<span className="text-[14px] font-semibold">{type.label}</span>
				</Checkbox>
				{assets.length > 0 ? (
					<Chip size="sm" variant="flat" color="success">
						On file
					</Chip>
				) : checked ? (
					// Ticked is the claim; what's missing is the proof.
					<Chip size="sm" variant="flat" color="danger">
						No scan
					</Chip>
				) : (
					<Chip size="sm" variant="flat" className="bg-default-100 text-default-500">
						Not claimed
					</Chip>
				)}
			</header>

			{missingProof && (
				<div className="flex items-center gap-2 rounded-lg bg-danger-50 px-3 py-2.5">
					<TriangleAlert className="w-4 h-4 shrink-0 text-danger" aria-hidden="true" />
					<span className="text-[13px] text-danger-700">
						Ticked but nothing is attached — it will not count as valid.
					</span>
				</div>
			)}

			{/* One scan per endorsement: upload once the box is ticked (the row
			    exists then) and nothing is attached yet. */}
			{(assets.length > 0 || checked) && (
				<AttachmentList
					items={assets}
					onDelete={item => onDeleteAsset(type.key, item)}
					onUpload={checked && assets.length === 0 ? file => onUploadAsset(type.key, file) : undefined}
					busy={busy}
					emptyText="Attach the signed logbook page."
					uploadLabel="Attach scan"
					policy="pdf"
				/>
			)}

			{!checked && assets.length === 0 && (
				<p className="text-[12.5px] text-default-400 m-0">
					Tick the endorsement to enable uploads.
				</p>
			)}
		</section>
	);
}

export default EndorsementCard;
